import { get as getCookie, getAll as getAllCookies, set as setCookie } from 'es-cookie';
import { parseQueryParameters } from 'bam-url';
import { deepGetOfType } from 'bam-deep-get';

const OT_COOKIE_KEYS = {
    BOX_CLOSED: 'OptanonAlertBoxClosed',
    CONSENT: 'OptanonConsent'
};

let prevConsent = '';
let loginConsentState = null;

/**
 * Check if OneTrust is active
 * @returns {Boolean} True = OneTrust active
 */
function isOneTrustActive() {
    return (window.Optanon);
}

/**
 * Check to see if the user consented to a particular group
 * @param {string} group The groups number
 * @returns {boolean} False if not active.  True if user accepts or OneTrust not present
 */
export function oneTrustIsActiveGroup(group) {
    return (window.OptanonActiveGroups && window.OptanonActiveGroups.match(group));
}

/**
 * Check to see if the previous Consent contains any items that were deactivated.
 * If they are found return true to signal a reload is required.
 * @returns {Boolean} If a restart is required due to a previously activated group being disabled.
 */
function checkForReload() {
    const prevActivedGroups = prevConsent.split(',');

    const deactivated = prevActivedGroups.filter(group => {
        if (!group) return false;

        return !(oneTrustIsActiveGroup(group));
    });

    return (deactivated.length);
}

/**
 * Add a function to be called when the user updates their OneTrust Consent
 * @param {Function} func = Function to be run when the user updates their consent
 */
export function addOneTrustOnConsentFunction(func) {
    if (!window.OptanonWrapperFunctions) {
        window.OptanonWrapperFunctions = [];
    }

    if (func && typeof func === 'function') {
        window.OptanonWrapperFunctions.push(func);
    }
}

/**
 * If there are any OptanonWrapperFunctions functions that were added after OneTrust loaded, this function will re-run
 * the OptanonWrapper method
 * @param {function} onConsentChanged - callback for consent change
 * @param {Object} options callbacks for onload and onerror
 */
export function initalizeOneTrust(onConsentChanged = () => {}, options = { onload: () => {}, onerror: () => {} }) {
    const { OneTrust } = window;

    if (OneTrust) {
        addOneTrustOnConsentFunction(()=>{
            if (!window.OneTrust.loaded) {
                // eslint-disable-next-line new-cap
                window.Optanon.OnConsentChanged(() => {
                    onConsentChanged();
                });
                window.OneTrust.loaded = true;
            }
        });

        const script = document.createElement('script');

        script.type = 'text/javascript';
        script.charset = 'UTF-8';
        script.defer = true;
        script.onerror = () => {
            options.onerror();
        };
        script.onload = () => {
            options.onload();
        };
        script.src = OneTrust.src;
        script.setAttribute('data-document-language', 'true');
        script.setAttribute('data-domain-script', OneTrust.guuid);

        document.head.appendChild(script);

        prevConsent = window.OptanonActiveGroups;

        //Adding in preventDetaults for OT prefernce enter links that go to href="#"
        const thirdPartyLinks = document.querySelectorAll('#onetrust-pc-sdk [href="#"]');

        thirdPartyLinks.forEach(link => {
            link.addEventListener('click', e => {
                e.preventDefault();
            });
        });
    }
}

/**
 * Takes the users AccountId and associates that Id with the user consent
 * @param {string} id - The user's D+ account Id or device id
 * @param {bool} isAuth - Login state
 * @param {string} countryCode - Users physical location
 */
export function syncOneTrustConsent(id, isAuth, countryCode) {
    const { OneTrustConsent } = window;

    if (isOneTrustActive()) {
        if (prevConsent === window.OptanonActiveGroups && loginConsentState === isAuth) return;

        let reload = false;

        if (prevConsent) {
            reload = checkForReload();
        }

        prevConsent = window.OptanonActiveGroups;
        loginConsentState = isAuth;

        if (OneTrustConsent) {
            OneTrustConsent.sendConsent(id, isAuth, countryCode, () => {
                if (reload) {
                    const cookies = getAllCookies();

                    for (const name in cookies) {
                        if (!Object.values(OT_COOKIE_KEYS).includes(name)) {
                            setCookie(name, cookies[name], { domain: '.disneyplus.com', expires: -1 });
                        }
                    }
                    window.location.reload();
                }
            });
        }
    }
}


/**
 * Activate all categories for fallback
 */
export function activateAllCategories() {
    window.OptanonActiveGroups = ',1,2,3,4,';
}

/**
 * If the Onetrust killswitch is on, this function will be used to get a local version of OptanonActiveGroups.
 * If the OneTrust cookie is already set us that to populate
 * If the fallback cookie is set activate all categories.
 */
export function setFallbackActiveGroup() {
    const fallbackCookieValue = getCookie('dssFallbackConsent');
    const oneTrustCookieValue = getCookie('OptanonConsent');

    if (oneTrustCookieValue) {
        const otConsent = parseQueryParameters(oneTrustCookieValue);
        const groups = decodeURIComponent(otConsent.groups).split(',');
        let activeGroups = '';

        groups.forEach(g => {
            if (/(:1)/.test(g)) {
                activeGroups += `,${g.substring(0, 1)}`;
            }
        });

        window.OptanonActiveGroups = `${activeGroups},`;
    } else if (fallbackCookieValue) {
        activateAllCategories();
    }
}

/**
 * If the current user is a kid's profile do not show the consent banner.  The next time
 * the user is on an adult profile, show the banner.
 * @param {object} profile The current active profile
 */
export function shouldBlockBanner(profile) {
    const closedExists = getCookie(OT_COOKIE_KEYS.BOX_CLOSED);
    const kidsModeEnabled = deepGetOfType(profile, 'attributes.kidsModeEnabled', Boolean, false);

    if (!closedExists && kidsModeEnabled) {
        setCookie(OT_COOKIE_KEYS.BOX_CLOSED, 'kidsAccountBlock', { domain: '.disneyplus.com' });
    } else if (closedExists === 'kidsAccountBlock' && !kidsModeEnabled) {
        setCookie(OT_COOKIE_KEYS.BOX_CLOSED, 'kidsAccountBlock', { domain: '.disneyplus.com', expires: -1 });
    }
}
